import { Link } from "@remix-run/react";
import { ExpirationOptions, type ExpirationType } from "@repo/api/src/schemas/order.schema";
import type { CameraPose } from "@workspace/blocks";
import { Check, CloudUpload } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { getUploadExperienceCopy } from "../../../data/uploadExperienceCopy";
import type { UseCreatePageContext } from "../../../hooks/useIndexPageContext";
import { useTranslation } from "../../../i18n";
import { getExpirationDateTime } from "../../../utils/datetime";
import { Button, Modal } from "../../ui";
import { UploadTransferMeter } from "../../upload/UploadTransferMeter";
import { FilePreview } from "./FilePreview";
import { UploadPreviewCameraPanel } from "./UploadPreviewCameraPanel";
import "./share-creation.css";

const flowCopy = {
	ja: {
		eyebrow: "共有リンクを作成",
		title: "3Dをアップロード。",
		lead: "タイトルと公開期限を確認したら、そのまま送信。完了すると共有リンクをすぐに使えます。",
		steps: ["ファイル", "共有設定", "リンク"],
		file: "選択中のファイル",
		presentation: "リンクに表示する内容",
		presentationNote: "タイトルは相手がリンクを開いたときにも表示されます。",
		access: "公開設定",
		accessNote: "期限を過ぎたリンクは閲覧できなくなります。",
		previewLead: "最初に表示する視点を確認",
		previewNote:
			"プレビューはこの端末で処理します。PLY・SOGは対応環境でWebGPUを使います。ファイルサイズが大きい場合、表示に時間がかかることがあります。",
		previewUnavailable: "プレビューを表示せずにリンクを作成できます。先に表示を確認することもできます。",
		create: "共有リンクを作成",
		sendingTitle: "3Dデータを送信しています。",
		sendingNote: "この画面を開いたままお待ちください。送信後にファイルを保存し、共有リンクを作成します。",
		doneTitle: "共有リンクを作成しました。",
		doneLead: "リンクをコピーして相手に送ると、ブラウザで3Dモデルを閲覧できます。",
		copy: "リンクをコピー",
		copied: "コピーしました",
		open: "リンクを開いて確認",
		next: "別のファイルを共有",
		optional: "説明・パスワードを設定",
	},
	en: {
		eyebrow: "Create a share",
		title: "Upload your 3D.",
		lead: "Check the title and access settings, then send it. Your share link is ready when the upload finishes.",
		steps: ["File", "Presentation", "Link"],
		file: "Selected file",
		presentation: "What the link shows",
		presentationNote: "Recipients also see this title when they open the link.",
		access: "Access settings",
		accessNote: "The link stops opening after its expiration.",
		previewLead: "Check the recipient's opening view",
		previewNote:
			"The preview runs on this device. PLY and SOG use WebGPU where available. Large files may take time to display.",
		previewUnavailable: "You can create a link without opening the preview, or check the file first.",
		create: "Create share link",
		sendingTitle: "Sending your 3D data.",
		sendingNote: "Keep this page open. Saving and link creation continue after the transfer.",
		doneTitle: "Your link is ready.",
		doneLead: "Copy and send it so someone can explore the 3D in a browser.",
		copy: "Copy link",
		copied: "Copied",
		open: "Open and check link",
		next: "Share another file",
		optional: "Add a description or password",
	},
} as const;

function formatSize(size: number) {
	return size < 1024 * 1024 ? `${(size / 1024).toFixed(1)} KiB` : `${(size / 1024 / 1024).toFixed(2)} MiB`;
}

export function ShareCreationFlow({ context }: { context: UseCreatePageContext }) {
	const { t, locale, localizedPath } = useTranslation();
	const copy = flowCopy[locale];
	const uploadCopy = getUploadExperienceCopy(locale);
	const { file, settings, setSettings, uploading, uploadProgress, uploadError, shortUrl, offline } = context;
	const [previewVisible, setPreviewVisible] = useState(false);
	const [cameraReady, setCameraReady] = useState(false);
	const [copied, setCopied] = useState(false);
	const [copyFailed, setCopyFailed] = useState(false);
	const titleRef = useRef<HTMLInputElement>(null);
	const urlRef = useRef<HTMLInputElement>(null);
	const stateRef = useRef<HTMLDivElement>(null);
	const pose = useRef<CameraPose | null>(null);
	const onCameraChange = useCallback((value: CameraPose) => {
		pose.current = value;
		setCameraReady(true);
	}, []);
	useEffect(() => {
		setPreviewVisible(!!file && file.size <= 100 * 1024 * 1024 && window.matchMedia("(min-width: 1024px)").matches);
		setCameraReady(false);
		pose.current = null;
		titleRef.current?.focus();
	}, [file]);
	useEffect(() => {
		if (uploading || uploadError || shortUrl) stateRef.current?.focus();
	}, [uploading, uploadError, shortUrl]);
	if (!file) return null;
	const is3d = !file.name.toLowerCase().endsWith(".html");
	const valid = context.canSubmit && !offline;
	const stage = uploadProgress?.stage || "preparing";
	const shareUrl = shortUrl ? `${typeof window !== "undefined" ? window.location.origin : ""}/viewer/${shortUrl}` : "";
	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl);
			setCopied(true);
			setCopyFailed(false);
		} catch {
			setCopyFailed(true);
			urlRef.current?.focus();
			urlRef.current?.select();
		}
	};
	return (
		<section className="share-creation" aria-label={t("upload.workspaceTitle")} data-testid="upload-workspace">
			<header className="creation-topbar">
				<Link to={localizedPath("/")} className="creation-brand">
					<img src="/images/gsplat-share-mark.png" alt="" />
					GSplat Share
				</Link>
				<nav aria-label={t("nav.main")}>
					<Link to={localizedPath("/mypage")}>{t("nav.dashboard")}</Link>
					<span>{copy.eyebrow}</span>
				</nav>
			</header>
			<ol className="creation-steps" aria-label={t("upload.workspaceTitle")}>
				{copy.steps.map((label, index) => (
					<li
						key={label}
						className={shortUrl ? "complete" : index === (uploading ? 2 : 1) ? "current" : ""}
						aria-current={!shortUrl && index === (uploading ? 2 : 1) ? "step" : undefined}
					>
						<span>0{index + 1}</span>
						{label}
					</li>
				))}
			</ol>
			{shortUrl ? (
				<div className="creation-result" ref={stateRef} tabIndex={-1}>
					<div className="upload-success-mark" aria-hidden="true">
						<Check size={34} strokeWidth={2.5} />
					</div>
					<p className="creation-kicker">03 / {copy.steps[2]}</p>
					<h1>{copy.doneTitle}</h1>
					<p>{copy.doneLead}</p>
					<div className="creation-linkbox">
						<input
							ref={urlRef}
							aria-label={t("shareButton.copyUrl")}
							readOnly
							value={shareUrl}
							onFocus={event => event.target.select()}
						/>
						<button type="button" onClick={copyLink}>
							{copied ? copy.copied : copy.copy} <span aria-hidden="true">↗</span>
						</button>
					</div>
					{copyFailed && <p role="alert">{t("viewer.copyFallback")}</p>}
					<p className="creation-save-note">{t("share.saveHint")}</p>
					<div className="creation-result-links">
						<a href={shareUrl} target="_blank" rel="noopener noreferrer">
							{copy.open} ↗
						</a>
						<Link to={localizedPath("/mypage")}>{t("share.manage")} ↗</Link>
					</div>
					<button className="creation-text-button" type="button" onClick={context.resetWorkspace}>
						{copy.next} →
					</button>
				</div>
			) : uploading ? (
				<div className="creation-progress" data-testid="upload-progress" ref={stateRef} tabIndex={-1}>
					<p className="creation-kicker">03 / {copy.steps[2]}</p>
					<h1>{copy.sendingTitle}</h1>
					<UploadTransferMeter file={file} progress={uploadProgress} />
					<p>{copy.sendingNote}</p>
					<button
						type="button"
						className="creation-text-button"
						onClick={context.handleCancelUpload}
						disabled={stage === "link"}
					>
						{t("uploadModal.cancelTransfer")}
					</button>
				</div>
			) : (
				<>
					<div className="creation-intro">
						<p className="creation-kicker">02 / {copy.steps[1]}</p>
						<h1>{copy.title}</h1>
						<p>{copy.lead}</p>
					</div>
					{offline && (
						<div className="creation-notice" role="alert">
							<strong>{t("uploadModal.errorOffline")}</strong>
							<p>{t("upload.offlineHint")}</p>
						</div>
					)}
					{uploadError && (
						<div className="creation-notice" role="alert" ref={stateRef} tabIndex={-1}>
							<strong>{uploadError.message}</strong>
							<p>{uploadError.recovery || t("uploadModal.retryHint")}</p>
							{uploadError.code && <small>{uploadError.code}</small>}
						</div>
					)}
					<div className="creation-file">
						<div>
							<span className="upload-ready-label">
								<Check size={14} aria-hidden="true" />
								{uploadCopy.ready}
							</span>
							<strong>{file.name}</strong>
							<small>
								{formatSize(file.size)} · {file.name.split(".").pop()?.toUpperCase()}
							</small>
							<small>{uploadCopy.replace}</small>
						</div>
						<div className="creation-file-actions">
							<button type="button" onClick={context.openFileDialog}>
								{t("upload.changeFile")}
							</button>
							<button type="button" onClick={context.removeFile}>
								{t("upload.removeFile")}
							</button>
						</div>
					</div>
					<div className="creation-grid">
						<div className="creation-main">
							<section className="creation-preview" aria-label={t("upload.previewTitle")}>
								<div className="creation-section-head">
									<div>
										<span>01</span>
										<h2>{copy.previewLead}</h2>
									</div>
									<button
										type="button"
										onClick={() => setPreviewVisible(value => !value)}
										aria-expanded={previewVisible}
									>
										{t(previewVisible ? "uploadModal.hidePreview" : "uploadModal.showPreview")}
									</button>
								</div>
								{previewVisible ? (
									<>
										<div className="creation-viewer">
											<FilePreview
												key={file.name + file.lastModified}
												file={file}
												heightClass="h-full"
												previewMode
												onCameraChange={onCameraChange}
												initialCameraPose={settings.customCameraPose ?? undefined}
												centerMode={settings.centerMode}
											/>
										</div>
										{is3d && (
											<UploadPreviewCameraPanel
												cameraReady={cameraReady}
												onSaveCameraPose={() => setSettings(p => ({ ...p, customCameraPose: pose.current }))}
												hasSavedPose={!!settings.customCameraPose}
												onClearSavedPose={() => setSettings(p => ({ ...p, customCameraPose: null }))}
											/>
										)}
									</>
								) : (
									<div className="creation-preview-closed">
										<p>{file.size > 100 * 1024 * 1024 ? t("upload.largePreviewHint") : copy.previewUnavailable}</p>
									</div>
								)}
								<p className="creation-caption">{copy.previewNote}</p>
							</section>
						</div>
						<form
							className="creation-form"
							data-testid="upload-settings"
							onSubmit={event => {
								event.preventDefault();
								if (valid) context.submitUpload();
							}}
						>
							<div className="creation-section-head">
								<div>
									<span>02</span>
									<h2>{copy.presentation}</h2>
								</div>
							</div>
							<label htmlFor="title">{t("uploadModal.titleLabel")}</label>
							<input
								ref={titleRef}
								id="title"
								value={settings.title}
								maxLength={100}
								required
								onChange={event => setSettings(p => ({ ...p, title: event.target.value }))}
								aria-invalid={!!context.fieldErrors.title}
								aria-describedby={context.fieldErrors.title ? "title-error" : undefined}
							/>
							{context.fieldErrors.title && (
								<small id="title-error" className="creation-error">
									{context.fieldErrors.title}
								</small>
							)}
							<p className="creation-field-note">{copy.presentationNote}</p>
							<div className="creation-rule" />
							<div className="creation-section-head">
								<div>
									<span>03</span>
									<h2>{copy.access}</h2>
								</div>
							</div>
							<label htmlFor="share-expiration">{t("uploadModal.expirationLabel")}</label>
							<select
								id="share-expiration"
								value={settings.expirationType}
								onChange={event => setSettings(p => ({ ...p, expirationType: event.target.value as ExpirationType }))}
							>
								{[
									ExpirationOptions.HOUR,
									ExpirationOptions.DAY,
									ExpirationOptions.WEEK,
									ExpirationOptions.UNLIMITED,
								].map(value => (
									<option key={value} value={value}>
										{getExpirationDateTime(value, locale, t)}
									</option>
								))}
							</select>
							<p className="creation-field-note">{copy.accessNote}</p>
							<details className="creation-optional">
								<summary>
									{copy.optional} <span aria-hidden="true">+</span>
								</summary>
								<div>
									<label htmlFor="description">{t("uploadModal.descriptionLabel")}</label>
									<textarea
										id="description"
										rows={3}
										maxLength={context.descriptionLimit}
										value={settings.description}
										onChange={event => setSettings(p => ({ ...p, description: event.target.value }))}
										aria-invalid={!!context.fieldErrors.description}
										aria-describedby="description-hint"
									/>
									<small id="description-hint">
										{t("upload.descriptionCount", {
											count: settings.description.length,
											max: context.descriptionLimit,
										})}
									</small>
									{context.fieldErrors.description && (
										<small id="description-error" className="creation-error">
											{context.fieldErrors.description}
										</small>
									)}
									<label className="creation-check">
										<input
											type="checkbox"
											checked={settings.hasPassword}
											onChange={event => setSettings(p => ({ ...p, hasPassword: event.target.checked }))}
										/>
										{t("uploadModal.passwordProtection")}
									</label>
									{settings.hasPassword && (
										<>
											<label htmlFor="password-input">{t("uploadModal.passwordLabel")}</label>
											<input
												id="password-input"
												type="password"
												autoComplete="new-password"
												minLength={3}
												maxLength={200}
												required
												value={settings.password}
												onChange={event => setSettings(p => ({ ...p, password: event.target.value }))}
												aria-invalid={!!context.fieldErrors.password}
											/>
											{context.fieldErrors.password && (
												<small className="creation-error">{context.fieldErrors.password}</small>
											)}
										</>
									)}
								</div>
							</details>
							<p className="creation-privacy">{t("upload.linkHint")}</p>
							<button className="creation-submit" type="submit" disabled={!valid}>
								<CloudUpload size={20} aria-hidden="true" />
								{copy.create} <span aria-hidden="true">↗</span>
							</button>
						</form>
					</div>
				</>
			)}
			<Modal
				open={context.discardOpen}
				onOpenChange={open => {
					if (!open) context.keepEditing();
				}}
				title={t("upload.discardTitle")}
				description={t(uploading ? "upload.discardTransfer" : "upload.discardDescription")}
				closeLabel={t("viewer.close")}
				footer={
					<>
						<Button variant="secondary" onClick={context.keepEditing}>
							{t("upload.keepEditing")}
						</Button>
						<Button variant="destructive" onClick={context.discard}>
							{t("upload.discard")}
						</Button>
					</>
				}
			>
				<p>{t("upload.discardHint")}</p>
			</Modal>
		</section>
	);
}
